import { useCallback, useLayoutEffect, useRef, useState } from "react";

// How much of the panel has to stay on screen. Without this a panel can be
// dragged past the edge and left with no grabbable area to pull it back.
const MIN_VISIBLE_X = 160;
const HANDLE_SAFE_HEIGHT = 48;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Constrains `offset` to the range that keeps the panel reachable. `rect` is
// where the panel sits while carrying `appliedOffset`, so removing that offset
// from the rect gives the untranslated position everything is measured from.
const clampOffset = (offset, rect, appliedOffset) => {
  const baseLeft = rect.left - appliedOffset.x;
  const baseTop = rect.top - appliedOffset.y;
  const minX = MIN_VISIBLE_X - baseLeft - rect.width;
  const maxX = window.innerWidth - MIN_VISIBLE_X - baseLeft;
  const minY = -baseTop;
  const maxY = window.innerHeight - HANDLE_SAFE_HEIGHT - baseTop;

  // A panel wider than the viewport inverts the bounds, so they are sorted
  // rather than trusted in order.
  const x = clamp(offset.x, Math.min(minX, maxX), Math.max(minX, maxX));
  const y = clamp(offset.y, Math.min(minY, maxY), Math.max(minY, maxY));

  // Returning the same object when nothing moved keeps this cheap to call from
  // effects without forcing a render.
  return x === offset.x && y === offset.y ? offset : { x, y };
};

// Makes a centered `position: fixed` panel draggable by a handle. The panel is
// positioned with `top/left: 50%` and this hook supplies the transform that
// both re-centers it and applies the drag offset, so the panel keeps its
// centered starting point without any layout measurement on mount.
//
// The offset lives for as long as the component stays mounted, so reopening the
// panel brings it back where it was left rather than snapping to the middle.
const useDraggablePanel = ({ isOpen }) => {
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // A panel parked against an edge can fall outside a shrinking viewport. This
  // also runs on open, since the offset outlives a close and the window may
  // have been resized in between, when no resize handler was listening.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const clampToViewport = () => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;
      // The rect was produced by the current offset, so it doubles as the
      // applied offset for the base-position maths.
      setOffset((current) => clampOffset(current, rect, current));
    };

    clampToViewport();
    window.addEventListener("resize", clampToViewport);
    return () => window.removeEventListener("resize", clampToViewport);
  }, [isOpen]);

  const handlePointerDown = useCallback(
    (event) => {
      // Buttons inside the handle (the close button) keep their own behaviour.
      if (event.button !== 0 || event.target.closest("button")) return;

      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Both the rect and the offset are frozen for the whole gesture so the
      // base position stays put while the panel moves under the pointer.
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startOffset: offset,
        rect,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsDragging(true);
    },
    [offset]
  );

  const handlePointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    setOffset(
      clampOffset(
        {
          x: drag.startOffset.x + (event.clientX - drag.startX),
          y: drag.startOffset.y + (event.clientY - drag.startY),
        },
        drag.rect,
        drag.startOffset
      )
    );
  }, []);

  const handlePointerUp = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
  }, []);

  const resetPosition = useCallback(() => setOffset({ x: 0, y: 0 }), []);

  return {
    panelRef,
    isDragging,
    resetPosition,
    panelStyle: {
      transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
    },
    handleProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
      onDoubleClick: resetPosition,
    },
  };
};

export default useDraggablePanel;
